/**
 * New Year social posts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearSocialPostsPack: ToilePack = {
    slug: 'new-year-social-posts',
    category: 'season',
    name: 'New Year social posts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'greeting', label: 'Greeting post', subject: 'A square post lettered "Happy New Year" in gold script over a burst of fireworks'},
        {key: 'review', label: 'Year in review', subject: 'A square post lettered "THIS YEAR" with three blank lines beneath and a star border'},
        {key: 'countdown', label: 'Countdown post', subject: 'A square post lettered "HOURS TO GO" beside a large blank circle for the number, confetti around it'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['for a story instead of feed', 'in pastel colours', 'as a carousel opener'],
    directionHint: 'try: in pastel colours',
    seo: {
        title: 'New Year Social Media Post: Greeting, Recap, Countdown',
        metaDescription: 'Nine new year social media posts — greeting square, year-in-review square, countdown square — lettered and framed, three styles.',
        h1: 'New year social media post: greeting, recap, countdown',
        tagline: 'Nine squares, three midnight subjects, three styles each.',
        intro: 'Square, feed-ready graphics for the New Year post: a greeting square, a year-in-review square, a countdown square, each lettered and framed to sit cleanly in a grid. Ask for a resolutions layout, a thank-you-2026 line, or your own copy, and the same three styles carry it.',
        useCases: [
            'an Instagram or Facebook feed post',
            'a business New Year greeting to followers',
            'a personal year-in-review recap post',
            'a scheduled countdown post series',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine square social posts — a greeting, a year-in-review layout, a countdown — lettered and framed, in three styles.'},
            {q: 'What size are the images?', a: 'Each renders as a square image, the standard feed-post shape for Instagram and Facebook.'},
            {q: 'Can I swap in my own text, like my business name?', a: 'The pack renders the wording as shown; swapping in your own line is a quick edit after downloading.'},
            {q: "Can I use these for my business's social accounts?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
            {q: 'Do I need to add my own photo to these?', a: 'No — each post is a complete standalone graphic; no photo is required to post it as-is.'},
        ],
    },
}
